import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { ApiResponse } from 'src/app/models/interface/CAM/baseResponseInterfaces';
import { CompanySetting } from 'src/app/models/interface/CAM/CompanyProfileInterface';
import { CamService } from 'src/app/services/camService/cam.service';
import { CompanyManagementService } from 'src/app/services/dataLayer/CAM/company-management.service';
import { ToastrApiService } from 'src/app/shared/common/toastr-api.service';
import { CompanyManagementContentService } from '../../services/company-management-content.service';
import { LocalSessionStorage } from 'src/app/models/interface/FlightSearch/CommanModel.interface';
import { CryptoService } from 'src/app/services/commonService/crypto.service';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatButton } from '@angular/material/button';

@Component({
    selector: 'app-company-settings',
    templateUrl: './company-settings.component.html',
    styleUrls: ['./company-settings.component.scss'],
    imports: [ReactiveFormsModule, MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, MatSlideToggle, MatButton]
})
export class CompanySettingsComponent implements OnInit, OnDestroy {
  companySettingForm!: FormGroup;
  buttonDisable: boolean = false;
  initialFormValues: any;
  private skipFormChangeCheck: boolean = true;
  private destroy$ = new Subject<void>();
  content:any;
  constructor(private _camService: CamService, private _FormBuilder: FormBuilder, private _ToastrApiService: ToastrApiService,private _CompanyManagementService: CompanyManagementService,private contentService:CompanyManagementContentService,  private readonly crypto: CryptoService,) {
    this.companySettingForm = this._FormBuilder.group({
      approvalAndPolicy: this._FormBuilder.group({
        enableGoOnLeave: false,
        enableMailToReportingManager: false,
      }), 
      bookings: this._FormBuilder.group({
        allowAutoTicketToSBT: false,
        enableManualCardEntry: false,
        allowRebookingTA: false,
        allowCancellationTA: false,
        allowCancellationEmployee: false,
        enableGuestSearch: false,
        enableMultiPassengerSearch: false,
        allowRebookingEmployees: true,
      }),
      extras: this._FormBuilder.group({
        enableSeatMap: false,
        enablePaidSeatMap: false,
        enablePaidMeal: false,
        enablePaidBaggage: false,
        enableFastTrack: false,
        enableLoungePass: false,
        enableMeetAndGreet: false
      }),
      flag: 'UPDATE'
    });

    // Listen for form changes
    this.companySettingForm.valueChanges.subscribe(() => {
      if (!this.skipFormChangeCheck) {
        this.checkFormChanges();
      }
    });
  }

  ngOnInit(): void {
    this.contentService.content$.pipe(takeUntil(this.destroy$)).subscribe((res:any)=>{
      this.content = res;
    })
    this.buttonDisable = true;

    this.getCompanySettingsData();
  }

  getCompanySettingsData() {
    this._camService.getCompanySetting().then((res:ApiResponse<CompanySetting>) => {
      if (res?.status === "Success" && res?.data) {
        const settings: CompanySetting = res.data; 
        this.companySettingForm.patchValue({
          approvalAndPolicy: {
            enableGoOnLeave: settings.enableGoOnLeave,
            enableMailToReportingManager: settings.enableMailToReportingManager,
          },
          bookings: {
            allowAutoTicketToSBT: settings.allowAutoTicketToSBT,
            enableManualCardEntry: settings.enableManualCardEntry,
            allowCancellationEmployee: settings.allowCancellationEmployee,
            allowRebookingTA: settings.allowRebookingTA,
            allowCancellationTA: settings.allowCancellationTA,
            enableGuestSearch: settings.enableGuestSearch,
            enableMultiPassengerSearch: settings.enableMultiPassengerSearch,
            allowRebookingEmployees: settings.allowRebookingEmployees,
          },
          extras: {
            enablePaidBaggage: settings.enablePaidBaggage,
            enablePaidMeal: settings.enablePaidMeal,
            enableSeatMap: settings.enableSeatMap,
            enablePaidSeatMap: settings.enablePaidSeatMap,
            enableFastTrack: settings.enableFastTrack,
            enableLoungePass: settings.enableLoungePass,
            enableMeetAndGreet: settings.enableMeetAndGreet
          },
          flag: 'UPDATE'
        });

        // Store initial form values after loading
        this.initialFormValues = JSON.parse(JSON.stringify(this.companySettingForm.value));
        this.skipFormChangeCheck = false; // ✅ Now changes can be tracked
        this.checkFormChanges(); // Ensure buttons stay disabled after initial set

        // Persist full API response to storage so rest of app gets updated data
        const companySettingsJson = JSON.stringify(res?.data);
        localStorage.setItem(LocalSessionStorage.COMPANY_SETTINGS, companySettingsJson);
        sessionStorage.setItem(LocalSessionStorage.COMPANY_SETTINGS, companySettingsJson);
      }
    }).catch((error) => {
      console.error(error);
       const message =
          error?.error?.message ||
          error?.message ||
          'Something went wrong. Please try again.';
      this._CompanyManagementService.view_error_message(message,error?.code);
      // this.skipFormChangeCheck = false;
      // this.checkFormChanges();
      this._ToastrApiService.show('error', 'Something went wrong while fetching Company Setting')

    });
  }

  checkFormChanges() {
    const currentValues = JSON.stringify(this.companySettingForm.value);
    const initialValues = JSON.stringify(this.initialFormValues);
    this.buttonDisable = currentValues === initialValues;
  }

  onCancel() {
    // Reset form to initial values
    this.buttonDisable = true;
    this.companySettingForm.patchValue(this.initialFormValues);
    this._ToastrApiService.show('warning', 'No changes were saved.')
    this._CompanyManagementService.company_management_companySetting('Cancel button','Cancel',0)//GTM use
  }


  onSave() {
    this._CompanyManagementService.company_management_companySetting('Save button','Save',0)//GTM use

    if (this.companySettingForm.invalid) {
      return
    }
    const companyId =
      sessionStorage.getItem(LocalSessionStorage.COMPANY_ID) ||
      localStorage.getItem(LocalSessionStorage.COMPANY_ID) ||
      '';

    const formValue = this.companySettingForm.value;
    const requestData: any = {
      flag: formValue?.flag || 'UPDATE',
      b2BCompanyId: companyId,
      B2BId: this.crypto.getValueFromKey('SUBAGENTID') as string,
      enableMailToReportingManager: !!formValue?.approvalAndPolicy?.enableMailToReportingManager,
      enableGoOnLeave: !!formValue?.approvalAndPolicy?.enableGoOnLeave,

      allowAutoTicketToSBT: !!formValue?.bookings?.allowAutoTicketToSBT,
      enableManualCardEntry: !!formValue?.bookings?.enableManualCardEntry,
      enableMultiPassengerSearch: !!formValue?.bookings?.enableMultiPassengerSearch,
      enableGuestSearch: !!formValue?.bookings?.enableGuestSearch,
      allowRebookingEmployees: !!formValue?.bookings?.allowRebookingEmployees,
      allowRebookingTA: !!formValue?.bookings?.allowRebookingTA,
      allowCancellationEmployee: !!formValue?.bookings?.allowCancellationEmployee,
      allowCancellationTA: !!formValue?.bookings?.allowCancellationTA,

      enableSeatMap: !!formValue?.extras?.enableSeatMap,
      enablePaidSeatMap: !!formValue?.extras?.enablePaidSeatMap,
      enablePaidMeal: !!formValue?.extras?.enablePaidMeal,
      enablePaidBaggage: !!formValue?.extras?.enablePaidBaggage,
      enableFastTrack: !!formValue?.extras?.enableFastTrack,
      enableLoungePass: !!formValue?.extras?.enableLoungePass,
      enableMeetAndGreet: !!formValue?.extras?.enableMeetAndGreet,
    };

    console.log(this.companySettingForm,"this.companySettingForm");
    console.log(requestData,"requestData");
    

    this._camService.updateCompanySettingV1(requestData).then((res) => {
       if (!res) {
        this._ToastrApiService.show('error', 'No response received from server.');
        return;
      }

      switch (res.status) {
        case 'Success':
          this._ToastrApiService.show('success', res.message || 'Company setting updated successfully.');
          this.getCompanySettingsData();
          break;

        default:
          this._ToastrApiService.show('error', res.message || 'Unexpected response from server.');
          break;
      }

      this.buttonDisable = true;
      this.initialFormValues = JSON.parse(JSON.stringify(this.companySettingForm.value));
    }).catch((error) => {
      console.error(error)
        const errorMessage =
        error?.error?.message ||
        error?.message ||
        'Something went wrong while saving the Company setting. Please try again later.';
      this._CompanyManagementService.view_error_message(errorMessage,error?.code);
      this._ToastrApiService.show('error', errorMessage)
    })

  }
ngOnDestroy(){
  this.destroy$.next();
  this.destroy$.complete();
}
}
